import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton";

import { chf } from "./financeFormat";
import { CASHFLOW_TREND_MONTHS, selectCashflowWindow } from "./financeSelectors";
import {
  compactAxisTick,
  SERIES_COLOR,
  shortPeriodLabel,
  toChartNumber,
} from "../../../pages/admin/revenueAnalytics/revenueAnalyticsFormat";

/**
 * "Zahlungseingänge" — money that actually ARRIVED, per month.
 *
 * THE NAME IS THE CONTRACT. Not "Cashflow": a cash flow has two directions and
 * this has one. Vantio holds no payment-dated outflow at all — `unit_costs` are
 * contractual, periodised operating costs with a frequency and no payment date
 * (`dashboard_cost_service.py:236-242`) — so an outflow series would have to be
 * built from plan figures on an axis they were never measured on. Not "Umsatz"
 * either: the revenue figures answer for the month the rent was FOR, and this
 * answers for the month the money LANDED. A payment arriving today can belong to
 * a rent month from last year.
 *
 * ONE AXIS: `bexio_invoice_payments.payment_date`, and nothing else. The backend
 * says so on the wire (`scope.cashflow_axis`) and refuses `issue_date`,
 * `due_date`, `rent_period`, the derived `paid_date` and a `total_paid` delta —
 * see `bexio_cashflow_service.py`. Nothing is reconstructed here.
 *
 * NO SECOND REQUEST. `cashflow_trend` already travels inside the
 * `revenue-analytics` response the KPI cards read.
 *
 * THE BARS ARE THE SELECTION (P3c). A range draws exactly the months it names —
 * three selected, three bars; twelve, twelve — and a cross-year window keeps the
 * calendar's order. A SINGLE month keeps its six-month run-up, because one bar
 * is not a series; that is the only case where the chart shows months the
 * heading does not name, and it is what this card has always done.
 *
 * Still no second request, and emphatically none per month: `cashflow_trend`
 * carries the whole observed history in the call the KPI cards already make, and
 * the card SELECTS from it. Selecting which of the backend's own monthly buckets
 * fall inside the window is not a recomputation — no figure is summed or scaled.
 *
 * SPARSE, AND IT STAYS SPARSE. The series holds only months with at least one
 * TRUSTED payment — one whose invoice's full row sum is corroborated by the
 * provider's own `total_paid`. A missing month means "we cannot vouch for
 * anything here", NOT "CHF 0": the payment mirror is upsert-only and its
 * completeness is explicitly unverified. So bars appear for the months the
 * backend returned and for no others. A zero-filled gap would be the browser
 * asserting something the truth owner declined to.
 *
 * That is also why this is a BarChart and not a line. A line drawn across a
 * missing month interpolates through it, which is the same invented claim in a
 * subtler form.
 *
 * NOT POINTER-ONLY. recharts tooltips need a hover, so every month in the chart
 * is repeated as text below it — that list, not the chart, is what a screen
 * reader and a keyboard user get.
 */

const CARD = "flex h-full flex-col rounded-xl border border-border bg-card p-4 shadow-sm";

function CashflowTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-md">
      <div className="mb-0.5 font-semibold text-card-foreground">{p.label}</div>
      <div className="text-muted-foreground">
        {/* The contract value, not the charted Number. */}
        {chf(p.raw) ?? "—"} · {p.paymentCount}{" "}
        {p.paymentCount === 1 ? "Zahlung" : "Zahlungen"}
      </div>
    </div>
  );
}

function CardShell({ children, subtitle }) {
  return (
    <section
      aria-labelledby="finance-cashflow-heading"
      className={CARD}
      data-testid="finance-cashflow-card"
    >
      <h2
        id="finance-cashflow-heading"
        className="text-[13px] font-semibold text-card-foreground"
      >
        Zahlungseingänge
      </h2>
      <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{subtitle}</p>
      {children}
    </section>
  );
}

/** The canonical subtitle, kept verbatim from the revenue-analytics card. */
const SUBTITLE = "Nach Zahlungsdatum – nicht nach Mietmonat.";

export default function FinanceCashflowCard({
  revenueAnalytics,
  window: periodWindow,
  // The month the business calendar is in right now, from the page's own helper.
  // Passed in rather than read from a clock here: a component that asks the
  // device what day it is has a second time source (Regel 51).
  currentPeriod = null,
  loading = false,
  onRetry,
}) {
  const error = revenueAnalytics?.error;

  if (loading) {
    return (
      <CardShell subtitle={SUBTITLE}>
        <div className="mt-3 space-y-2" aria-busy="true">
          <Skeleton className="h-[170px] w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </CardShell>
    );
  }

  if (error) {
    return (
      <CardShell subtitle={SUBTITLE}>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <p role="alert" className="text-[12px] text-destructive">
            Zahlungseingänge konnten nicht geladen werden.
          </p>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="rounded-md border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
            >
              Erneut laden
            </button>
          ) : null}
        </div>
      </CardShell>
    );
  }

  const rows = selectCashflowWindow(revenueAnalytics?.data?.cashflow_trend, periodWindow);
  const data = rows.map((r) => ({
    ...r,
    label: shortPeriodLabel(r),
    value: toChartNumber(r.received),
  }));

  // The selected period ENDS in the business calendar's current month — so its
  // last bar is a running total, not a closed month. Stated, never extrapolated.
  // The test is on the window's END for the same reason it always was on the
  // month: an earlier month inside a range is closed, and only the last one can
  // still be filling up.
  const endsInCurrentMonth =
    Boolean(periodWindow) &&
    Boolean(currentPeriod) &&
    periodWindow.to_year === currentPeriod.year &&
    periodWindow.to_month === currentPeriod.month;

  return (
    <CardShell subtitle={SUBTITLE}>
      {data.length === 0 ? (
        <p className="mt-4 text-[12px] text-muted-foreground">
          Es liegen keine verifizierten Zahlungseingänge vor.
        </p>
      ) : (
        <>
          <div className="mt-3 h-[170px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                <CartesianGrid
                  stroke="currentColor"
                  className="text-border"
                  strokeDasharray="3 4"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11 }}
                  stroke="currentColor"
                  className="text-muted-foreground"
                />
                <YAxis
                  tickFormatter={compactAxisTick}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11 }}
                  stroke="currentColor"
                  className="text-muted-foreground"
                />
                <Tooltip content={<CashflowTooltip />} cursor={false} />
                <Bar
                  dataKey="value"
                  name="Zahlungseingänge"
                  fill={SERIES_COLOR.cash}
                  radius={[3, 3, 0, 0]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* The same figures as text. See the docstring: the chart alone is
              pointer-only, and these months are the information. */}
          <ul
            className="mt-3 list-none space-y-1 p-0 text-[11px]"
            data-testid="finance-cashflow-months"
          >
            {data.map((r) => (
              <li
                key={`${r.year}-${r.month}`}
                className="flex items-center justify-between gap-3"
              >
                <span className="text-muted-foreground">{r.label}</span>
                <span className="font-medium text-card-foreground">
                  {chf(r.raw) ?? "—"}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="mt-3 text-[10px] leading-snug text-muted-foreground">
        {/* Why a month can be absent, said once rather than drawn as a zero. */}
        {periodWindow?.isRange
          ? "Monate des gewählten Zeitraums."
          : `Bis zu ${CASHFLOW_TREND_MONTHS} Monate bis einschliesslich des gewählten Monats.`}{" "}
        Monate ohne verifizierte Zahlung werden nicht dargestellt.
        {endsInCurrentMonth
          ? periodWindow?.isRange
            ? " Der letzte Monat des Zeitraums läuft noch."
            : " Der gewählte Monat läuft noch."
          : ""}
      </p>
    </CardShell>
  );
}
